import { useState } from 'react';
import PropTypes from 'prop-types';
import { TimelineOutlined } from '@mui/icons-material';
import { Box, Tab, Tabs } from '@mui/material';
import { useTranslation } from 'react-i18next';
import ActivityEvolutionChart from './ActivityEvolutionChart';
import HeartRateEvolutionChart from './HeartRateEvolutionChart';
import MeasurementsEvolutionChart from './MeasurementsEvolutionChart';
import OverviewCard from './OverviewCard';
import SleepEvolutionChart from './SleepEvolutionChart';
import WearableActivityEvolutionChart from './WearableActivityEvolutionChart';

const TAB_DEFINITIONS = [
    { value: 'training-plans', translationKey: 'trainingPlans' },
    { value: 'activity', translationKey: 'activity' },
    {
        value: 'heart-rate',
        translationKey: 'heartRate'
    },
    { value: 'measurements', translationKey: 'measurements' },
    {
        value: 'sleep',
        translationKey: 'sleep'
    }
];

const PERIODS = [
    { value: '7d', translationKey: 'sevenDays' },
    { value: '30d', translationKey: 'thirtyDays' },
    { value: '90d', translationKey: 'ninetyDays' },
    { value: 'all', translationKey: 'sinceBeginning' }
];

const getSelectorStyles = (compact = false) => ({
    minHeight: 30,
    bgcolor: '#FFFFFF',
    border: '1px solid #D7DDE2',
    borderRadius: 2.25,
    boxShadow: '0 1px 4px rgba(38, 50, 56, 0.12)',
    overflow: 'visible',
    '& .MuiTabs-scroller': { overflow: 'visible !important' },
    '& .MuiTabs-flexContainer': { overflow: 'visible' },
    '& .MuiTabs-indicator': {
        zIndex: 0,
        height: compact ? 'calc(100% + 4px)' : 'calc(100% + 8px)',
        bottom: compact ? -2 : -4,
        bgcolor: '#0A579B',
        borderRadius: 1.75,
        boxShadow: '0 3px 8px rgba(10, 87, 155, 0.28)',
        transform: compact ? 'scaleX(1.02)' : 'scaleX(1.04)',
        transition:
            'left 240ms cubic-bezier(0.4, 0, 0.2, 1), right 240ms cubic-bezier(0.4, 0, 0.2, 1), width 240ms cubic-bezier(0.4, 0, 0.2, 1)'
    },
    '& .MuiTab-root': {
        zIndex: 1,
        minWidth: compact ? 52 : 0,
        minHeight: 28,
        px: compact ? 1.5 : 1.75,
        py: 0.5,
        overflow: 'visible',
        color: '#263238',
        fontSize: '0.75rem',
        fontWeight: 700,
        letterSpacing: 0,
        lineHeight: 1,
        textTransform: 'none',
        transition: 'color 180ms ease',
        '&.Mui-selected': { color: '#FFFFFF' }
    }
});

const RehabilitationEvolutionCard = ({
    patientUuid,
    patientCreated,
    plans,
    plansLoading,
    plansError
}) => {
    const { t } = useTranslation();
    const visibleTabs = TAB_DEFINITIONS;
    const [activeTab, setActiveTab] = useState('training-plans');
    const [period, setPeriod] = useState('30d');
    const [measurementSource, setMeasurementSource] = useState('patient');
    const [activityMetric, setActivityMetric] = useState('steps');
    const selectedTab = visibleTabs.some((tab) => tab.value === activeTab)
        ? activeTab
        : visibleTabs[0].value;

    return (
        <OverviewCard
            title={t('userPage.overview.rehabilitationEvolution')}
            icon={<TimelineOutlined fontSize="small" />}
            minHeight={400}
            action={
                <Tabs
                    value={period}
                    onChange={(_event, nextPeriod) => setPeriod(nextPeriod)}
                    aria-label={t('userPage.overview.rehabilitation.period')}
                    sx={getSelectorStyles(true)}
                >
                    {PERIODS.map((item) => (
                        <Tab
                            key={item.value}
                            value={item.value}
                            label={t(
                                `userPage.overview.rehabilitation.${item.translationKey}`
                            )}
                        />
                    ))}
                </Tabs>
            }
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: 2
                }}
            >
                <Tabs
                    value={selectedTab}
                    onChange={(_event, nextTab) => setActiveTab(nextTab)}
                    aria-label={t('userPage.overview.rehabilitation.tabs')}
                    sx={{
                        alignSelf: 'flex-start',
                        ...getSelectorStyles()
                    }}
                >
                    {visibleTabs.map((tab) => (
                        <Tab
                            key={tab.value}
                            value={tab.value}
                            label={t(
                                `userPage.overview.rehabilitation.${tab.translationKey}`
                            )}
                        />
                    ))}
                </Tabs>
                {selectedTab === 'activity' && (
                    <Tabs
                        value={activityMetric}
                        onChange={(_, value) => setActivityMetric(value)}
                        aria-label={t('userPage.overview.rehabilitation.activityMetric')}
                        sx={getSelectorStyles(true)}
                    >
                        <Tab
                            value="steps"
                            label={t('userPage.overview.rehabilitation.stepsChart.steps')}
                        />
                        <Tab
                            value="distance"
                            label={t(
                                'userPage.overview.rehabilitation.distanceChart.distance'
                            )}
                        />
                    </Tabs>
                )}
                {selectedTab === 'measurements' && (
                    <Tabs
                        value={measurementSource}
                        onChange={(_, value) => setMeasurementSource(value)}
                        aria-label={t('userPage.measurements.sourceSelector')}
                        sx={getSelectorStyles(true)}
                    >
                        <Tab
                            value="patient"
                            label={t('userPage.measurements.patientSource')}
                        />
                        <Tab
                            value="clinician"
                            label={t('userPage.measurements.clinicianSource')}
                        />
                    </Tabs>
                )}
            </Box>

            <Box
                role="tabpanel"
                aria-label={t(
                    `userPage.overview.rehabilitation.${
                        visibleTabs.find((tab) => tab.value === selectedTab)
                            ?.translationKey || 'activity'
                    }`
                )}
                sx={{ flex: 1, minHeight: 210, mt: 1.5 }}
            >
                {selectedTab === 'training-plans' && (
                    <ActivityEvolutionChart
                        patientUuid={patientUuid}
                        plans={plans}
                        plansLoading={plansLoading}
                        plansError={plansError}
                        period={period}
                    />
                )}
                {selectedTab === 'activity' && (
                    <WearableActivityEvolutionChart
                        metric={activityMetric}
                        key={patientUuid}
                        patientUuid={patientUuid}
                        patientCreated={patientCreated}
                        period={period}
                    />
                )}
                {selectedTab === 'heart-rate' && (
                    <HeartRateEvolutionChart
                        key={patientUuid}
                        patientUuid={patientUuid}
                        patientCreated={patientCreated}
                        period={period}
                    />
                )}
                {selectedTab === 'measurements' && (
                    <MeasurementsEvolutionChart
                        source={measurementSource}
                        patientUuid={patientUuid}
                        patientCreated={patientCreated}
                        plans={plans}
                        plansLoading={plansLoading}
                        plansError={plansError}
                        period={period}
                    />
                )}
                {selectedTab === 'sleep' && (
                    <SleepEvolutionChart
                        key={patientUuid}
                        patientUuid={patientUuid}
                        period={period}
                        patientCreated={patientCreated}
                    />
                )}
            </Box>
        </OverviewCard>
    );
};

RehabilitationEvolutionCard.propTypes = {
    patientUuid: PropTypes.string.isRequired,
    patientCreated: PropTypes.string,
    plans: PropTypes.arrayOf(PropTypes.object).isRequired,
    plansLoading: PropTypes.bool.isRequired,
    plansError: PropTypes.bool.isRequired
};

export default RehabilitationEvolutionCard;
